import { supabase } from '../config/supabase';

/** One practice sentence: Chinese + optional English (for translate in quiz). */
export interface HanziExampleSentence {
  zh: string;
  en?: string;
}

export function exampleZh(s: HanziExampleSentence): string {
  return (s?.zh ?? '').trim();
}

export function exampleEn(s: HanziExampleSentence): string | undefined {
  const t = (s?.en ?? '').trim();
  return t || undefined;
}

/**
 * Generate example sentences via Edge Function when DB has none.
 * No user sign-in: uses the project anon key as Bearer JWT so the gateway accepts the call (same key as in the client bundle).
 */
export const ensureHanziExampleSentences = async (
  practiceWordId: string
): Promise<HanziExampleSentence[]> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
  const bearer = session?.access_token ?? anonKey;
  const { data, error } = await supabase.functions.invoke<{
    sentences?: HanziExampleSentence[];
    error?: string;
  }>('ensure-hanzi-example-sentences', {
    body: { practiceWordId },
    headers: { Authorization: `Bearer ${bearer}` },
  });
  // Non-2xx responses often still parse JSON into `data`; prefer that message over generic invoke error.
  if (data && typeof data === 'object' && 'error' in data && data.error) {
    throw new Error(String(data.error));
  }
  if (error) throw error;
  const sentences = data?.sentences;
  if (!Array.isArray(sentences) || sentences.length === 0) {
    throw new Error('No sentences returned');
  }
  return parseExampleSentences(sentences);
};

export interface HanziWord {
  id: string;
  tuteeId: string;
  studentName?: string;
  character: string;
  pinyin?: string;
  notes?: string;
  /** @deprecated Use exampleSentences */
  exampleSentence?: string;
  /** Sentences for fill-in-blank; may include English for translation toggle. */
  exampleSentences?: HanziExampleSentence[];
  displayOrder: number;
  status: 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
  /** MOE metadata when from curriculum */
  moeLevel?: string;
  moeLessonNumber?: number;
  moeType?: 'read' | 'write';
}

export interface HanziMoeCharacter {
  id: string;
  level: string;
  lessonNumber: number;
  type: 'read' | 'write';
  character: string;
  displayOrder: number;
  exampleSentences?: HanziExampleSentence[];
}

export interface HanziCharacterProgress {
  character: string;
  attemptCount: number;
  correctCount: number;
}

export interface HanziStudentProgress {
  id: string;
  tuteeId: string;
  studentName: string;
  attemptCount: number;
  correctCount: number;
  highestStreak: number;
  currentStreak: number;
  lastPracticeDate: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface HanziDailyLog {
  id: string;
  tuteeId: string;
  studentName: string;
  calendarDate: string;
  sessionCompleted: boolean;
  charactersPracticed: number;
  createdAt: string;
  updatedAt: string;
}

/** One step in the “wrong answer” writing sequence (Hanzi Hero). */
export type HanziRemedialStepType = 'writer_guided' | 'grid_with_model' | 'grid_free';

export const HANZI_REMEDIAL_STEP_TYPES: readonly HanziRemedialStepType[] = [
  'writer_guided',
  'grid_with_model',
  'grid_free',
] as const;

/** Default: stroke guide → grid with 范字 → blank grid. */
export const DEFAULT_REMEDIAL_STEPS: HanziRemedialStepType[] = [
  'writer_guided',
  'grid_with_model',
  'grid_free',
];

export interface HanziConfig {
  charactersPerSession?: number;
  gridStyle?: 'jiugong' | 'tianzige' | 'mizige';
  showModelCharacter?: boolean;
  prizeWheelSegments?: { label: string; weight: number }[];
  /** Minimum consecutive-day streak (after today’s check-in) required to show the prize wheel. Default 1 = always after record. */
  prizeWheelMinStreak?: number;
  /** 'moe' = MOE curriculum only, 'custom' = hanzi_words only, 'both' = merge */
  characterSource?: 'moe' | 'custom' | 'both';
  /** MOE levels to include (multi-select). Takes precedence over legacy `moeLevel` when set. */
  moeLevels?: string[];
  /** Legacy single level; used when `moeLevels` is missing or empty */
  moeLevel?: string;
  /** MOE curriculum types to include (multi-select). Legacy `moeType` used when unset. */
  moeTypes?: ('read' | 'write')[];
  /** Legacy single type */
  moeType?: 'read' | 'write';
  /** Optional: specific lesson numbers to include (null = all) */
  moeLessons?: number[];
  /** Pause between strokes when HanziWriter plays the full character animation (Show me). Default 1000 ms. */
  writerDelayBetweenStrokesMs?: number;
  /** If true (default), trace boxes show a faint character outline. If false, stroke quiz still grades shape but with no outline hint. */
  writerQuizShowOutline?: boolean;
  /**
   * Per-student remedial steps after a wrong recall (student key = lowercase name, "" = default when no named students).
   * Each step is one round before continuing.
   */
  remedialStepsByStudent?: Record<string, HanziRemedialStepType[]>;
}

const REMEDIAL_TYPE_SET = new Set<string>(HANZI_REMEDIAL_STEP_TYPES);

export function normalizeRemedialSteps(arr: unknown): HanziRemedialStepType[] {
  if (!Array.isArray(arr) || arr.length === 0) return [...DEFAULT_REMEDIAL_STEPS];
  const out = arr.filter((x): x is HanziRemedialStepType => REMEDIAL_TYPE_SET.has(String(x)));
  return out.length > 0 ? out.slice(0, 12) : [...DEFAULT_REMEDIAL_STEPS];
}

export function cycleHanziRemedialStepType(current: HanziRemedialStepType): HanziRemedialStepType {
  const i = HANZI_REMEDIAL_STEP_TYPES.indexOf(current);
  const ix = i < 0 ? 0 : (i + 1) % HANZI_REMEDIAL_STEP_TYPES.length;
  return HANZI_REMEDIAL_STEP_TYPES[ix]!;
}

export function normalizeRemedialStepsByStudent(
  raw: unknown
): Record<string, HanziRemedialStepType[]> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Record<string, HanziRemedialStepType[]> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const key = k.toLowerCase().trim();
    const norm = normalizeRemedialSteps(v);
    if (norm.length) out[key] = norm;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Resolved plan for the practising student (lowercase key in `remedialStepsByStudent`). */
export function getRemedialPlanForStudent(
  config: HanziConfig,
  studentName: string | undefined
): HanziRemedialStepType[] {
  const key = (studentName ?? '').toLowerCase().trim();
  const map = config.remedialStepsByStudent;
  if (map && key && Array.isArray(map[key]) && map[key]!.length > 0) {
    return normalizeRemedialSteps(map[key]);
  }
  if (map && Array.isArray(map['']) && map['']!.length > 0) {
    return normalizeRemedialSteps(map['']);
  }
  return [...DEFAULT_REMEDIAL_STEPS];
}

const DEFAULT_CONFIG: HanziConfig = {
  charactersPerSession: 5,
  gridStyle: 'tianzige',
  showModelCharacter: true,
  characterSource: 'both',
  moeLevel: 'P5',
  moeLevels: ['P5'],
  moeType: 'write',
  moeTypes: ['write'],
  prizeWheelSegments: [
    { label: 'Star', weight: 1 },
    { label: 'Heart', weight: 1 },
    { label: 'Trophy', weight: 1 },
  ],
  prizeWheelMinStreak: 1,
  writerDelayBetweenStrokesMs: 1000,
  writerQuizShowOutline: true,
};

/** Normalize DB JSONB: legacy `["中文"]` or `[{"zh":"...","en":"..."}]`. */
export function parseExampleSentences(val: unknown): HanziExampleSentence[] {
  if (!Array.isArray(val)) return [];
  const out: HanziExampleSentence[] = [];
  for (const x of val) {
    if (typeof x === 'string' && x.trim()) {
      out.push({ zh: x.trim() });
      continue;
    }
    if (x && typeof x === 'object' && typeof (x as { zh?: unknown }).zh === 'string') {
      const zh = (x as { zh: string }).zh.trim();
      if (!zh) continue;
      const enRaw = (x as { en?: unknown }).en;
      const en = typeof enRaw === 'string' && enRaw.trim() ? enRaw.trim() : undefined;
      out.push(en ? { zh, en } : { zh });
    }
  }
  return out;
}

/** One line per sentence; optional `中文 | English` (space-pipe-space). */
export function parseExampleSentencesFromTextarea(text: string): HanziExampleSentence[] {
  const sep = ' | ';
  const out: HanziExampleSentence[] = [];
  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const i = trimmed.indexOf(sep);
    if (i >= 0) {
      const zh = trimmed.slice(0, i).trim();
      const en = trimmed.slice(i + sep.length).trim();
      if (zh) out.push(en ? { zh, en } : { zh });
    } else {
      out.push({ zh: trimmed });
    }
  }
  return out;
}

export function serializeExampleSentencesForTextarea(sentences: HanziExampleSentence[]): string {
  return sentences
    .map((s) => {
      const zh = exampleZh(s);
      const en = exampleEn(s);
      return en ? `${zh} | ${en}` : zh;
    })
    .join('\n');
}

const VALID_MOE_LEVELS = new Set(['P1', 'P2', 'P3', 'P4', 'P5', 'P6']);

const sortMoeLevels = (levels: string[]) =>
  [...levels].sort((a, b) => parseInt(a.slice(1), 10) - parseInt(b.slice(1), 10));

/** Resolve which MOE primary levels to load (legacy `moeLevel` or `moeLevels`). */
export function resolveMoeLevels(c: Partial<HanziConfig>): string[] {
  if (Array.isArray(c.moeLevels) && c.moeLevels.length > 0) {
    const u = [
      ...new Set(
        c.moeLevels.filter((x): x is string => typeof x === 'string' && VALID_MOE_LEVELS.has(x))
      ),
    ];
    if (u.length > 0) return sortMoeLevels(u);
  }
  const one =
    typeof c.moeLevel === 'string' && VALID_MOE_LEVELS.has(c.moeLevel) ? c.moeLevel : 'P5';
  return [one];
}

/** Resolve 识读 / 识写 inclusion (legacy `moeType` or `moeTypes`). */
export function resolveMoeTypes(c: Partial<HanziConfig>): ('read' | 'write')[] {
  if (Array.isArray(c.moeTypes) && c.moeTypes.length > 0) {
    const u = [
      ...new Set(c.moeTypes.filter((x): x is 'read' | 'write' => x === 'read' || x === 'write')),
    ];
    if (u.length > 0) {
      const rank: Record<'read' | 'write', number> = { write: 0, read: 1 };
      return u.slice().sort((a, b) => rank[a] - rank[b]);
    }
  }
  const one = c.moeType === 'read' || c.moeType === 'write' ? c.moeType : 'write';
  return [one];
}

export const getHanziConfig = (componentConfig?: Record<string, unknown>): HanziConfig => {
  const merged = { ...DEFAULT_CONFIG, ...(componentConfig as HanziConfig) };
  const moeLevels = resolveMoeLevels(merged);
  const moeTypes = resolveMoeTypes(merged);
  const remedialStepsByStudent = normalizeRemedialStepsByStudent(merged.remedialStepsByStudent);
  return {
    ...merged,
    moeLevels,
    moeLevel: moeLevels[0] ?? 'P5',
    moeTypes,
    moeType: moeTypes[0] ?? 'write',
    remedialStepsByStudent,
  };
};

/** Fetch practice characters based on config (MOE and/or custom) */
export const fetchPracticeWords = async (
  tuteeId: string,
  studentName: string | undefined,
  config: HanziConfig
): Promise<HanziWord[]> => {
  const source = config.characterSource ?? 'both';
  const moeLevels = resolveMoeLevels(config);
  const moeTypes = resolveMoeTypes(config);

  const results: HanziWord[] = [];
  const seenChars = new Set<string>();

  if ((source === 'moe' || source === 'both') && moeLevels.length > 0 && moeTypes.length > 0) {
    const moeChars = await fetchMoeCharacters(moeLevels, moeTypes, config.moeLessons);
    for (const mc of moeChars) {
      if (!seenChars.has(mc.character)) {
        seenChars.add(mc.character);
        results.push({
          id: `moe-${mc.id}`,
          tuteeId,
          studentName,
          character: mc.character,
          pinyin: undefined,
          notes: undefined,
          exampleSentences: mc.exampleSentences,
          displayOrder: mc.displayOrder,
          status: 'active',
          createdAt: '',
          updatedAt: '',
          moeLevel: mc.level,
          moeLessonNumber: mc.lessonNumber,
          moeType: mc.type,
        });
      }
    }
  }

  if (source === 'custom' || source === 'both') {
    const customWords = await fetchHanziWords(tuteeId, studentName, true);
    for (const w of customWords) {
      if (!seenChars.has(w.character)) {
        seenChars.add(w.character);
        results.push(w);
      }
    }
  }

  return results.sort((a, b) => a.displayOrder - b.displayOrder);
};

export const fetchHanziWords = async (
  tuteeId: string,
  studentName?: string,
  activeOnly = true
): Promise<HanziWord[]> => {
  try {
    let query = supabase
      .from('hanzi_words')
      .select('*')
      .eq('tutee_id', tuteeId)
      .order('display_order', { ascending: true });

    if (studentName) {
      query = query.eq('student_name', studentName);
    } else {
      query = query.is('student_name', null);
    }
    if (activeOnly) {
      query = query.eq('status', 'active');
    }

    const { data, error } = await query;
    if (error) throw error;

    return (data || []).map((item) => {
      const sentences = parseExampleSentences(item.example_sentences);
      const legacy = item.example_sentence && typeof item.example_sentence === 'string'
        ? item.example_sentence.trim()
        : '';
      return {
        id: item.id,
        tuteeId: item.tutee_id,
        studentName: item.student_name,
        character: item.character,
        pinyin: item.pinyin,
        notes: item.notes,
        exampleSentence: legacy || undefined,
        exampleSentences: sentences.length > 0 ? sentences : legacy ? [{ zh: legacy }] : undefined,
        displayOrder: item.display_order ?? 0,
        status: item.status || 'active',
        createdAt: item.created_at,
        updatedAt: item.updated_at,
      };
    });
  } catch (error) {
    console.error('Error fetching hanzi words:', error);
    throw error;
  }
};

export const createHanziWord = async (input: {
  tuteeId: string;
  studentName?: string;
  character: string;
  pinyin?: string;
  notes?: string;
  exampleSentence?: string;
  exampleSentences?: HanziExampleSentence[];
  displayOrder?: number;
}): Promise<HanziWord> => {
  const sentences = input.exampleSentences?.length
    ? input.exampleSentences
    : input.exampleSentence
      ? [{ zh: input.exampleSentence }]
      : [];
  try {
    const { data, error } = await supabase
      .from('hanzi_words')
      .insert({
        tutee_id: input.tuteeId,
        student_name: input.studentName ?? null,
        character: input.character,
        pinyin: input.pinyin ?? null,
        notes: input.notes ?? null,
        example_sentence: input.exampleSentence ?? null,
        example_sentences: sentences.length > 0 ? sentences : null,
        display_order: input.displayOrder ?? 0,
        status: 'active',
      })
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      tuteeId: data.tutee_id,
      studentName: data.student_name,
      character: data.character,
      pinyin: data.pinyin,
      notes: data.notes,
      exampleSentence: data.example_sentence,
      exampleSentences: parseExampleSentences(data.example_sentences),
      displayOrder: data.display_order ?? 0,
      status: data.status || 'active',
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  } catch (error) {
    console.error('Error creating hanzi word:', error);
    throw error;
  }
};

export const updateHanziWord = async (
  id: string,
  updates: Partial<{
    character: string;
    pinyin: string;
    notes: string;
    exampleSentence: string;
    exampleSentences: HanziExampleSentence[];
    displayOrder: number;
    status: string;
  }>
): Promise<HanziWord> => {
  try {
    const payload: Record<string, unknown> = {};
    if (updates.character !== undefined) payload.character = updates.character;
    if (updates.pinyin !== undefined) payload.pinyin = updates.pinyin;
    if (updates.notes !== undefined) payload.notes = updates.notes;
    if (updates.exampleSentence !== undefined) payload.example_sentence = updates.exampleSentence;
    if (updates.exampleSentences !== undefined)
      payload.example_sentences = updates.exampleSentences.length > 0 ? updates.exampleSentences : null;
    if (updates.displayOrder !== undefined) payload.display_order = updates.displayOrder;
    if (updates.status !== undefined) payload.status = updates.status;
    payload.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('hanzi_words')
      .update(payload)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      tuteeId: data.tutee_id,
      studentName: data.student_name,
      character: data.character,
      pinyin: data.pinyin,
      notes: data.notes,
      exampleSentence: data.example_sentence,
      exampleSentences: parseExampleSentences(data.example_sentences),
      displayOrder: data.display_order ?? 0,
      status: data.status || 'active',
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  } catch (error) {
    console.error('Error updating hanzi word:', error);
    throw error;
  }
};

export const deleteHanziWord = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase.from('hanzi_words').delete().eq('id', id);
    if (error) throw error;
  } catch (error) {
    console.error('Error deleting hanzi word:', error);
    throw error;
  }
};

export const fetchMoeCharacters = async (
  levels: string[],
  types: ('read' | 'write')[],
  lessonNumbers?: number[]
): Promise<HanziMoeCharacter[]> => {
  if (!levels.length || !types.length) return [];
  try {
    let query = supabase
      .from('hanzi_moe_characters')
      .select('*')
      .in('level', levels)
      .in('type', types)
      .order('level', { ascending: true })
      .order('lesson_number', { ascending: true })
      .order('display_order', { ascending: true });

    if (lessonNumbers && lessonNumbers.length > 0) {
      query = query.in('lesson_number', lessonNumbers);
    }

    const { data, error } = await query;
    if (error) throw error;

    return (data || []).map((item) => ({
      id: item.id,
      level: item.level,
      lessonNumber: item.lesson_number,
      type: item.type,
      character: item.character,
      displayOrder: item.display_order ?? 0,
      exampleSentences: parseExampleSentences(item.example_sentences),
    }));
  } catch (error) {
    console.error('Error fetching MOE characters:', error);
    throw error;
  }
};

/** Update example sentences for an MOE character. */
export const updateMoeCharacterExampleSentences = async (
  level: string,
  lessonNumber: number,
  type: 'read' | 'write',
  character: string,
  exampleSentences: HanziExampleSentence[]
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('hanzi_moe_characters')
      .update({
        example_sentences: exampleSentences.length > 0 ? exampleSentences : [],
      })
      .eq('level', level)
      .eq('lesson_number', lessonNumber)
      .eq('type', type)
      .eq('character', character);
    if (error) throw error;
  } catch (error) {
    console.error('Error updating MOE character sentences:', error);
    throw error;
  }
};

/** Fetch per-character progress for given characters. Returns map of character -> stats. */
export const fetchCharacterProgress = async (
  tuteeId: string,
  studentName: string,
  characters: string[]
): Promise<Record<string, { attemptCount: number; correctCount: number }>> => {
  if (characters.length === 0) return {};
  try {
    const { data, error } = await supabase
      .from('hanzi_character_progress')
      .select('character, attempt_count, correct_count')
      .eq('tutee_id', tuteeId)
      .eq('student_name', studentName ?? '')
      .in('character', [...new Set(characters)]);

    if (error) throw error;
    const map: Record<string, { attemptCount: number; correctCount: number }> = {};
    for (const row of data || []) {
      map[row.character] = {
        attemptCount: row.attempt_count ?? 0,
        correctCount: row.correct_count ?? 0,
      };
    }
    return map;
  } catch (error) {
    console.error('Error fetching character progress:', error);
    throw error;
  }
};

/** All per-character stats for a student (admin / reports). */
export const fetchAllStudentCharacterProgress = async (
  tuteeId: string,
  studentName: string
): Promise<HanziCharacterProgress[]> => {
  try {
    const { data, error } = await supabase
      .from('hanzi_character_progress')
      .select('character, attempt_count, correct_count')
      .eq('tutee_id', tuteeId)
      .eq('student_name', studentName ?? '')
      .order('character', { ascending: true });

    if (error) throw error;
    return (data || []).map((row) => ({
      character: row.character,
      attemptCount: row.attempt_count ?? 0,
      correctCount: row.correct_count ?? 0,
    }));
  } catch (error) {
    console.error('Error fetching all character progress:', error);
    throw error;
  }
};

/** Aggregates for student dashboard quiz card (all students under this tutee). */
export type HanziTuteeDashboardStats = {
  /** Character-level accuracy: sum(correct) / sum(attempts), capped at 100%. */
  accuracyPct: number | null;
  /** Total completed practice sessions (hanzi_student_progress.attempt_count per student). */
  practiceSessions: number;
};

export const fetchHanziTuteeDashboardStats = async (
  tuteeId: string
): Promise<HanziTuteeDashboardStats> => {
  try {
    const [charRes, studRes] = await Promise.all([
      supabase
        .from('hanzi_character_progress')
        .select('attempt_count, correct_count')
        .eq('tutee_id', tuteeId),
      supabase
        .from('hanzi_student_progress')
        .select('attempt_count')
        .eq('tutee_id', tuteeId),
    ]);

    if (charRes.error) throw charRes.error;
    if (studRes.error) throw studRes.error;

    let charAttempts = 0;
    let charCorrect = 0;
    for (const row of charRes.data || []) {
      charAttempts += row.attempt_count ?? 0;
      charCorrect += row.correct_count ?? 0;
    }
    const accuracyPct =
      charAttempts > 0
        ? Math.min(100, Math.round((charCorrect / charAttempts) * 100))
        : null;

    let practiceSessions = 0;
    for (const row of studRes.data || []) {
      practiceSessions += row.attempt_count ?? 0;
    }

    return { accuracyPct, practiceSessions };
  } catch (error) {
    console.error('Error fetching Hanzi tutee dashboard stats:', error);
    return { accuracyPct: null, practiceSessions: 0 };
  }
};

/** Record one attempt for a character. Row created on first practice. */
export const recordCharacterAttempt = async (
  tuteeId: string,
  studentName: string,
  character: string,
  correct: boolean
): Promise<void> => {
  try {
    await supabase.rpc('record_hanzi_character_attempt', {
      p_tutee_id: tuteeId,
      p_student_name: studentName ?? '',
      p_character: character,
      p_correct: correct,
    });
  } catch (error) {
    console.error('Error recording character attempt:', error);
    throw error;
  }
};

export const fetchStudentProgress = async (
  tuteeId: string,
  studentName: string
): Promise<HanziStudentProgress | null> => {
  try {
    const { data, error } = await supabase
      .from('hanzi_student_progress')
      .select('*')
      .eq('tutee_id', tuteeId)
      .eq('student_name', studentName)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    return {
      id: data.id,
      tuteeId: data.tutee_id,
      studentName: data.student_name,
      attemptCount: data.attempt_count ?? 0,
      correctCount: data.correct_count ?? 0,
      highestStreak: data.highest_streak ?? 0,
      currentStreak: data.current_streak ?? 0,
      lastPracticeDate: data.last_practice_date,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  } catch (error) {
    console.error('Error fetching student progress:', error);
    throw error;
  }
};

export const upsertStudentProgress = async (
  tuteeId: string,
  studentName: string,
  updates: {
    attemptCount?: number;
    correctCount?: number;
    highestStreak?: number;
    currentStreak?: number;
    lastPracticeDate?: string | null;
  }
): Promise<HanziStudentProgress> => {
  try {
    const { data, error } = await supabase
      .from('hanzi_student_progress')
      .upsert(
        {
          tutee_id: tuteeId,
          student_name: studentName,
          ...(updates.attemptCount !== undefined && { attempt_count: updates.attemptCount }),
          ...(updates.correctCount !== undefined && { correct_count: updates.correctCount }),
          ...(updates.highestStreak !== undefined && { highest_streak: updates.highestStreak }),
          ...(updates.currentStreak !== undefined && { current_streak: updates.currentStreak }),
          ...(updates.lastPracticeDate !== undefined && {
            last_practice_date: updates.lastPracticeDate,
          }),
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'tutee_id,student_name' }
      )
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      tuteeId: data.tutee_id,
      studentName: data.student_name,
      attemptCount: data.attempt_count ?? 0,
      correctCount: data.correct_count ?? 0,
      highestStreak: data.highest_streak ?? 0,
      currentStreak: data.current_streak ?? 0,
      lastPracticeDate: data.last_practice_date,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  } catch (error) {
    console.error('Error upserting student progress:', error);
    throw error;
  }
};

export const recordDailyCheckin = async (
  tuteeId: string,
  charactersPracticed: number,
  studentName: string = ''
): Promise<HanziDailyLog> => {
  const calendarDate = new Date().toISOString().slice(0, 10);
  try {
    const { data, error } = await supabase
      .from('hanzi_daily_log')
      .upsert(
        {
          tutee_id: tuteeId,
          student_name: studentName,
          calendar_date: calendarDate,
          session_completed: true,
          characters_practiced: charactersPracticed,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'tutee_id,student_name,calendar_date' }
      )
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      tuteeId: data.tutee_id,
      studentName: data.student_name ?? '',
      calendarDate: data.calendar_date,
      sessionCompleted: data.session_completed,
      charactersPracticed: data.characters_practiced ?? 0,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  } catch (error) {
    console.error('Error recording daily checkin:', error);
    throw error;
  }
};

export const fetchDailyLogs = async (
  tuteeId: string,
  studentName?: string,
  limit = 30
): Promise<HanziDailyLog[]> => {
  try {
    let query = supabase
      .from('hanzi_daily_log')
      .select('*')
      .eq('tutee_id', tuteeId)
      .order('calendar_date', { ascending: false })
      .limit(limit);

    if (studentName !== undefined) {
      query = query.eq('student_name', studentName);
    }

    const { data, error } = await query;
    if (error) throw error;

    return (data || []).map((item) => ({
      id: item.id,
      tuteeId: item.tutee_id,
      studentName: item.student_name ?? '',
      calendarDate: item.calendar_date,
      sessionCompleted: item.session_completed,
      charactersPracticed: item.characters_practiced ?? 0,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
    }));
  } catch (error) {
    console.error('Error fetching daily logs:', error);
    throw error;
  }
};

/** Record session results and update student progress + daily log */
export const recordSessionResults = async (
  tuteeId: string,
  studentName: string,
  charactersPracticed: number,
  correctCount: number
): Promise<{ streak: number; progress: HanziStudentProgress }> => {
  const calendarDate = new Date().toISOString().slice(0, 10);
  const studentKey = studentName || '';

  await recordDailyCheckin(tuteeId, charactersPracticed, studentKey);
  const logs = await fetchDailyLogs(tuteeId, studentKey, 60);
  const newStreak = computeStreak(logs);

  const existing = await fetchStudentProgress(tuteeId, studentKey);
  const prevHighest = existing?.highestStreak ?? 0;
  const newHighest = Math.max(prevHighest, newStreak);

  const progress = await upsertStudentProgress(tuteeId, studentKey, {
    attemptCount: (existing?.attemptCount ?? 0) + 1,
    correctCount: (existing?.correctCount ?? 0) + correctCount,
    highestStreak: newHighest,
    currentStreak: newStreak,
    lastPracticeDate: calendarDate,
  });

  return { streak: newStreak, progress };
};

export const computeStreak = (logs: HanziDailyLog[]): number => {
  if (logs.length === 0) return 0;
  const today = new Date().toISOString().slice(0, 10);
  const sorted = [...logs].sort((a, b) => b.calendarDate.localeCompare(a.calendarDate));
  if (sorted[0].calendarDate !== today) {
    const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    if (sorted[0].calendarDate !== yesterday) return 0;
  }
  let streak = 0;
  let expectDate = new Date().toISOString().slice(0, 10);
  for (const log of sorted) {
    if (log.calendarDate === expectDate) {
      streak++;
      expectDate = new Date(new Date(expectDate).getTime() - 86400000).toISOString().slice(0, 10);
    } else if (log.calendarDate < expectDate) {
      break;
    }
  }
  return streak;
};
